import { useDemo } from "../zustand/DemoZustand";
import { Schloss, Sprechblase, Textmarker } from "./Symbole";
import { nameVon, notizenZuDatei } from "../hilfen/auswertung";
import { springeZuAnnotation } from "../hilfen/textauswahl";
import { kurzesDatum, uhrzeit } from "../hilfen/datum";

/*
  Die Notizen der Fraktion zu einer Vorlage.

  Das ist der Randbereich des Dokuments, nicht der Chat. Eine Notiz ist
  eine Feststellung — „find ich gut“, „hier fehlt etwas“ —, keine
  Gesprächseröffnung. Es gibt deshalb bewusst keine Antworten darauf und auch
  keine Zustimmung.

  Der Grund steht in KOMMUNIKATION.md: Ein Dokument liest man allein.
  Dreizehn Leute lesen dieselbe Vorlage an dreizehn verschiedenen Abenden. Wer
  sonntags um 22 Uhr an den Rand schreibt, erwartet keine Antwort — er legt
  etwas hin. Wer über eine Notiz reden will, zieht sie in den Chat; dafür
  ist der Knopf da.

  Die Liste hängt an der *Datei*, nicht am Tagesordnungspunkt. Das ist Absicht:
  Tauscht die Verwaltung die Vorlage gegen eine neue Fassung, bleiben die
  Notizen bei der Fassung, zu der sie gehören.
*/

export default function Notizliste({
  dateiId,
  aufTextstelle,
  aufInDenChat,
  aufBearbeiten,
  /*
    Die Liste hängt inzwischen an zweierlei: an einer Vorlage der Verwaltung
    und an einem Dokument, das die Fraktion selbst schreibt. „Randbemerkungen
    zur Vorlage" wäre im zweiten Fall schlicht falsch.
  */
  gegenstand = "Vorlage",
}) {
  const { stammdaten, zustand, ich } = useDemo();

  if (!dateiId) {
    return (
      <div className="karte">
        <p className="leise">
          Zu diesem Punkt liegt keine Vorlage vor — es gibt also nichts, woran man etwas notieren
          könnte.
        </p>
      </div>
    );
  }

  const notizen = notizenZuDatei(zustand, ich.id, dateiId);

  if (notizen.length === 0) {
    return (
      <div className="karte">
        <p className="leise">In der Fraktion hat noch niemand etwas an den Rand geschrieben.</p>
        <p className="klein leise" style={{ marginTop: "var(--luft-3)" }}>
          Wähl im Reiter „{gegenstand === "Vorlage" ? "Vorlage" : "Text"}“ eine Textstelle aus —
          dann kannst du eine Notiz dazu schreiben.
        </p>
      </div>
    );
  }

  return (
    <div className="notizen">
      <p className="notizen__hinweis">
        Randbemerkungen zu{gegenstand === "Vorlage" ? "r Vorlage" : "m Dokument"}, in der
        Reihenfolge des Textes. Hier wird nicht geantwortet — wer über eine Stelle reden
        möchte, gibt sie in den Chat.
      </p>

      {notizen.map((a) => (
        <Notiz
          key={a.id}
          notiz={a}
          name={nameVon(stammdaten, a.verfasserId, ich.id)}
          eigen={a.verfasserId === ich.id}
          aufTextstelle={aufTextstelle}
          aufInDenChat={aufInDenChat}
          aufBearbeiten={aufBearbeiten}
        />
      ))}
    </div>
  );
}

function Notiz({ notiz, name, eigen, aufTextstelle, aufInDenChat, aufBearbeiten }) {
  /*
    Steht die Vorlage schon offen — auf einem breiten Bildschirm etwa —,
    genügt Scrollen. Sonst muss der Sitzungsraum den Reiter wechseln; das kann
    nur er, deshalb die Rückfrage nach oben.
  */
  function zurStelle() {
    if (springeZuAnnotation(notiz.id)) return;
    if (aufTextstelle) aufTextstelle(notiz);
  }

  return (
    <article className="notiz">
      <div className={`notiz__band notiz__band--${notiz.farbe}`} aria-hidden="true" />

      <div className="notiz__inhalt">
        <p className="notiz__wer">
          {name} · {kurzesDatum(notiz.zeit)}, {uhrzeit(notiz.zeit)}
          {notiz.sichtbarkeit === "privat" && (
            <span className="notiz__privat">
              <Schloss groesse={13} />
              Nur für dich
            </span>
          )}
        </p>

        <blockquote className="notiz__zitat">„{notiz.zitat}“</blockquote>
        <p className="notiz__text">{notiz.notiz}</p>

        <div className="notiz__fuss">
          <button className="notiz__knopf" onClick={zurStelle}>
            <Textmarker groesse={15} />
            Zur Textstelle
          </button>

          {/*
            Nur zeigen, was auch etwas tut. Ein Knopf ohne Handler stürzte
            beim Antippen ab — und ein Knopf, der nichts kann, gehört nach
            der Regel des Steckbriefs ohnehin nicht in die Oberfläche.
          */}
          {aufInDenChat && (
            <button className="notiz__knopf" onClick={() => aufInDenChat(notiz)}>
              <Sprechblase groesse={15} />
              In den Chat
            </button>
          )}

          {eigen && aufBearbeiten && (
            <button className="notiz__knopf" onClick={() => aufBearbeiten(notiz)}>
              Bearbeiten
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
